import { defineNuxtRouteMiddleware, useNuxtApp } from '#imports'

import { useRedirections } from '~/scopes/product-core/components/ProductDetailPageBase/composables/useRedirections/useRedirections'
import { useFetchPickers } from '~/scopes/product-core/composables/useFetch/useFetchPickers/useFetchPickers'
import { useFetchProduct } from '~/scopes/product-core/composables/useFetch/useFetchProduct/useFetchProduct'

/**
 * Universal route middleware for the Product Detail Page.
 *
 * Runs on both server and client for every navigation to a PDP route.
 *
 * ## Two responsibilities
 *
 * ### 1. Populate the cache (block navigation until data is ready)
 *
 * Awaiting both fetches here ensures:
 * - On SSR: redirect composables see populated refs and can issue real 3xx
 *   responses before Cloudflare caches the page.
 * - On client navigation: the component never mounts with an empty loading
 *   state — it renders immediately with the resolved data.
 *
 * ### 2. Run URL-correcting redirects
 *
 * After the fetches resolve, `useRedirections` is called with the populated
 * fetch results to check for and issue any necessary redirects (invalid grade,
 * missing grade, stale slug, unavailable grade).
 *
 * The result of `useRedirections` is **returned** from the middleware so Nuxt's
 * router can trigger the actual redirect. Just calling `navigateTo` without
 * returning it from a middleware is a silent no-op.
 *
 * `nuxtApp.runWithContext(...)` restores the Nuxt request context that is lost
 * after the `await Promise.all` above.
 * See: https://nuxt.com/docs/api/composables/use-nuxt-app#runwithcontext
 */
export default defineNuxtRouteMiddleware(
  async function fetchDataAndRunRedirectionsIfNeeded(to) {
    const nuxtApp = useNuxtApp()

    // Phase 1: populate cache and block navigation until data is ready
    const productFetch = useFetchProduct(to)
    const pickersFetch = useFetchPickers(to)
    await Promise.all([productFetch, pickersFetch])

    // Phase 2: run URL corrections with Nuxt context restored after await
    return nuxtApp.runWithContext(() =>
      useRedirections(productFetch, pickersFetch, to),
    )
  },
)
